import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { 
  Camera, ArrowLeft, Upload, FileImage, Download, 
  CheckCircle, XCircle, Clock, AlertCircle, Eye, Plus, ExternalLink
} from 'lucide-react';

const Deliverables = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [deliverable, setDeliverable] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [processingRevision, setProcessingRevision] = useState(false);
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const config = { withCredentials: true, headers };

  // Handle return from Stripe payment for additional revision
  useEffect(() => {
    const handlePaidRevision = async () => {
      if (searchParams.get('revision_paid') === 'true') {
        const notes = searchParams.get('notes') || '';
        try {
          await axios.post(`${API}/revisions/complete-paid-revision`, {
            booking_id: bookingId,
            notes: notes
          }, config);
          toast.success('Additional revision requested successfully!');
          fetchDeliverables();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to complete revision request');
        }
        // Clean up URL
        navigate(`/deliverables/${bookingId}`, { replace: true });
      }
    };
    handlePaidRevision();
  }, [searchParams, bookingId]);

  useEffect(() => {
    fetchDeliverables();
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`, config);
      setBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
    }
  };

  const fetchDeliverables = async () => {
    try {
      const response = await axios.get(`${API}/deliverables/${bookingId}`, config);
      setDeliverable(response.data);
    } catch (error) {
      console.error('Error fetching deliverables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    try {
      const uploadedFiles = [];
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload/deliverable`, formData, {
          withCredentials: true,
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        uploadedFiles.push({ filename: res.data.filename, url: res.data.url, preview_url: res.data.preview_url || null, type: res.data.type });
      }

      await axios.post(`${API}/deliverables/${bookingId}/upload`, { files: uploadedFiles }, config);

      toast.success('Deliverables uploaded successfully!');
      setSelectedFiles([]);
      fetchDeliverables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/deliverables/${bookingId}/approve`, {}, config);
      toast.success('Deliverables approved! Funds released to creative.');
      fetchDeliverables();
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approval failed');
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionNotes.trim()) {
      toast.error('Please provide revision notes');
      return;
    }

    // Check if this is an additional (paid) revision
    const isAdditional = (deliverable?.revisions_used || 0) >= (deliverable?.revisions_included || 0);

    setProcessingRevision(true);
    try {
      if (isAdditional && deliverable?.additional_revision_rate > 0) {
        // Create Stripe checkout session for paid revision
        const response = await axios.post(`${API}/revisions/create-checkout-session`, {
          booking_id: bookingId,
          origin_url: window.location.origin,
          notes: revisionNotes
        }, config);
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
        return;
      }
      
      // Free revision - proceed directly
      const response = await axios.post(`${API}/deliverables/${bookingId}/request-revision`, {
        notes: revisionNotes,
        is_additional: isAdditional
      }, config);

      toast.success(isAdditional ? 'Additional revision requested' : 'Revision requested');
      setRevisionOpen(false);
      setRevisionNotes('');
      fetchDeliverables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    } finally {
      setProcessingRevision(false);
    }
  };

  const getStatusInfo = () => {
    switch (deliverable?.status) {
      case 'pending_upload':
        return { color: 'bg-yellow-100 text-yellow-700', icon: Clock, text: 'Awaiting Upload' };
      case 'pending_review':
        return { color: 'bg-blue-100 text-blue-700', icon: Eye, text: 'Pending Review' };
      case 'revision_requested':
        return { color: 'bg-orange-100 text-orange-700', icon: AlertCircle, text: 'Revision Requested' };
      case 'approved':
        return { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'Approved' };
      default:
        return { color: 'bg-gray-100 text-gray-700', icon: Clock, text: 'Unknown' };
    }
  };

  const isCreative = user?.user_id === deliverable?.creative_id;
  const isBusiness = user?.user_id === deliverable?.business_id;

  const handleDownload = async (url, filename, fileId) => {
    try {
      const proxyUrl = `${API}/deliverables/${bookingId}/download/${fileId}`;
      const res = await axios.get(proxyUrl, { ...config, responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('Download error:', err);
      window.open(url, '_blank');
    }
  };
  const statusInfo = getStatusInfo();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f5f2]">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading deliverables...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Header */}
      <div className="bg-white border-b border-[#e4e1db]">
        <div className="max-w-4xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/bookings" className="text-[#a0a0a0] hover:text-[#0e0e0e] transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0]">Project Files</p>
                <h1 className="font-display font-normal text-[22px] text-[#0e0e0e] tracking-[-0.3px] leading-tight">Deliverables</h1>
                <p className="text-[#a0a0a0] text-[13px]">{booking?.project_title}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Status Card */}
        <div className="bg-white rounded-[20px] p-6 mb-6 border border-[#e4e1db]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${statusInfo.color}`}>
                <statusInfo.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-[#0e0e0e]">{statusInfo.text}</p>
                <p className="text-[13px] text-[#a0a0a0]">
                  {deliverable?.revisions_used || 0} of {deliverable?.revisions_included || 0} revisions used
                </p>
              </div>
            </div>
            {deliverable?.auto_approve_at && deliverable?.status === 'pending_review' && (
              <div className="text-right">
                <p className="text-[13px] text-[#a0a0a0]">Auto-approves on</p>
                <p className="font-semibold text-[#0e0e0e]">{new Date(deliverable.auto_approve_at).toLocaleDateString()}</p>
              </div>
            )}
          </div>
        </div>

        {/* Revision Notes */}
        {deliverable?.status === 'revision_requested' && deliverable?.revision_notes && (
          <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-semibold text-orange-800">Revision Requested</p>
                <p className="text-orange-700 mt-1">{deliverable.revision_notes}</p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Section (Creative) */}
        {isCreative && (deliverable?.status === 'pending_upload' || deliverable?.status === 'revision_requested') && (
          <div className="bg-white rounded-[20px] border border-[#e4e1db] p-6 mb-6">
            <h2 className="font-display font-normal text-[18px] text-[#0e0e0e] mb-2">Upload Deliverables</h2>
            <p className="text-muted-foreground mb-4 text-sm">
              Upload your files directly from your computer. All file types accepted (images, videos, documents, ZIPs). Max 200MB per file.
            </p>

            <div className="space-y-4">
              {/* Drop zone / file picker */}
              <label
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                  isDragging
                    ? 'border-ink bg-[#f0ede8]'
                    : 'border-[#e4e1db] hover:border-ink hover:bg-[#f7f5f2]'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <Upload className={`w-6 h-6 mb-2 ${isDragging ? 'text-ink' : 'text-[#a0a0a0]'}`} />
                <span className="text-sm font-medium text-[#5a5a5a]">Click to select files</span>
                <span className="text-xs text-[#a0a0a0] mt-1">or drag and drop</span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </label>

              {/* Selected files list */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#f7f5f2] rounded-[10px] border border-[#e4e1db]">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileImage className="w-4 h-4 text-[#a0a0a0] flex-shrink-0" />
                        <span className="text-[13px] text-[#0e0e0e] truncate">{file.name}</span>
                        <span className="text-[11px] text-[#a0a0a0] flex-shrink-0">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                      </div>
                      <button onClick={() => removeSelectedFile(index)} className="text-red-500 hover:text-red-700 text-sm ml-3 flex-shrink-0">
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={handleUpload}
                className="w-full rounded-full bg-primary hover:bg-primary/90"
                disabled={uploading || selectedFiles.length === 0}
                data-testid="upload-deliverables-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : `Submit ${selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}` : 'Deliverables'}`}
              </Button>
            </div>
          </div>
        )}

        {/* Files List */}
        {deliverable?.files?.length > 0 && (
          <div className="bg-white rounded-[20px] border border-[#e4e1db] p-6 mb-6">
            <h2 className="font-display font-normal text-[18px] text-[#0e0e0e] mb-4">Submitted Files</h2>

            <div className="space-y-3">
              {deliverable.files.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 p-4 rounded-[12px] border border-[#e4e1db] hover:border-[#0e0e0e] hover:bg-[#f7f5f2] transition-colors cursor-pointer"
                  onClick={() => setPreviewFile(file)}
                >
                  <div className="w-12 h-12 rounded-[10px] bg-[#f7f5f2] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {file.type === 'image' ? (
                      <img
                        src={booking?.payment_status !== 'released' && file.preview_url ? file.preview_url : file.url}
                        alt={file.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Eye className="w-6 h-6 text-[#a0a0a0]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{file.filename || 'View Deliverable'}</p>
                    <p className="text-[11px] text-[#a0a0a0]">
                      {file.type}
                      {file.uploaded_at && ` · ${new Date(file.uploaded_at).toLocaleString()}`}
                    </p>
                  </div>
                  <Eye className="w-5 h-5 text-[#a0a0a0] flex-shrink-0" />
                </div>
              ))}
            </div>

            {/* Client Actions */}
            {isBusiness && deliverable.status === 'pending_review' && (
              <div className="mt-6 space-y-4">
                <div className="flex gap-3">
                  {(deliverable.revisions_used || 0) < (deliverable.revisions_included || 0) ? (
                    <Button
                      onClick={() => setRevisionOpen(true)}
                      variant="outline"
                      className="flex-1 rounded-full"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Request Revision ({(deliverable.revisions_included || 0) - (deliverable.revisions_used || 0)} left)
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setRevisionOpen(true)}
                      variant="outline"
                      className="flex-1 rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Request Additional Revision 
                      {deliverable.additional_revision_rate > 0 && ` ($${deliverable.additional_revision_rate})`}
                    </Button>
                  )}
                  <Button
                    onClick={handleApprove}
                    className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
                    data-testid="approve-deliverables-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve & Release Payment
                  </Button>
                </div>
                
                {/* Info about revisions */}
                {(deliverable.revisions_used || 0) >= (deliverable.revisions_included || 0) && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm text-amber-700">
                      You've used all {deliverable.revisions_included} included revisions. 
                      {deliverable.additional_revision_rate > 0 
                        ? ` Additional revisions are available at $${deliverable.additional_revision_rate} each.`
                        : ' Contact the creative to discuss additional revisions.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Download (after approval) */}
            {isBusiness && deliverable.status === 'approved' && (
              <div className="mt-6">
                <div className="p-4 rounded-xl bg-green-50 border border-green-200 mb-4">
                  <div className="flex items-center gap-3 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Deliverables approved! Funds have been released.</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {deliverable.files.map((file, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleDownload(file.url, file.filename, file.file_id)}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity"
                    >
                      <Download className="w-4 h-4" />
                      {file.filename}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {(!deliverable?.files || deliverable.files.length === 0) && !isCreative && (
          <div className="bg-white rounded-[20px] border border-[#e4e1db] p-12 text-center">
            <Upload className="w-16 h-16 mx-auto text-[#e4e1db] mb-4" />
            <h3 className="font-display font-normal text-[20px] text-[#0e0e0e] mb-2">No deliverables yet</h3>
            <p className="text-[13px] text-[#a0a0a0]">The creative hasn't uploaded any files yet</p>
          </div>
        )}
      </div>

      {/* Revision Dialog */}
      <Dialog open={revisionOpen} onOpenChange={setRevisionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {(deliverable?.revisions_used || 0) >= (deliverable?.revisions_included || 0) 
                ? 'Request Additional Revision' 
                : 'Request Revision'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {(deliverable?.revisions_used || 0) >= (deliverable?.revisions_included || 0) ? (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-amber-800 text-sm">
                  You've used all {deliverable?.revisions_included || 0} included revisions. 
                  {deliverable?.additional_revision_rate > 0 
                    ? ` This additional revision will cost $${deliverable.additional_revision_rate}. You'll be redirected to payment.`
                    : ' Contact the creative to discuss additional revisions.'}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">
                Please describe what changes you need. You have {(deliverable?.revisions_included || 0) - (deliverable?.revisions_used || 0)} free revisions remaining.
              </p>
            )}
            <Textarea
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              placeholder="Describe the changes needed..."
              rows={5}
              data-testid="revision-notes-input"
            />
            <Button
              onClick={handleRequestRevision}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
              disabled={processingRevision}
              data-testid="submit-revision-btn"
            >
              {processingRevision 
                ? 'Processing...' 
                : (deliverable?.revisions_used || 0) >= (deliverable?.revisions_included || 0) && deliverable?.additional_revision_rate > 0
                  ? `Pay $${deliverable.additional_revision_rate} & Request Revision`
                  : 'Submit Revision Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewFile(null)}
        >
          <div className="max-w-4xl max-h-[90vh]">
            {previewFile.type === 'image' ? (
              <img
                src={booking?.payment_status !== 'released' && previewFile.preview_url ? previewFile.preview_url : previewFile.url}
                alt={previewFile.filename}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            ) : previewFile.type === 'video' ? (
              <video
                src={previewFile.url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="bg-white rounded-xl p-8 text-center" onClick={(e) => e.stopPropagation()}>
                <FileImage className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="font-semibold mb-2">{previewFile.filename}</p>
                <a
                  href={previewFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Open in new tab
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Deliverables;
